import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../types/translation/Translator';
import { countries } from '../../../../../util/country';
import { UnitSystem, UnitType } from '../../../../../types/uom';
import { FileContext } from '../../../../../context/FileContext';
import CustomFieldInput from '../../../Common/CustomFieldInput';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { CustomFieldEntityType, WithCustomFields } from '../../../../../types/custom_field/customField';
import { ImagePicker } from '../../../../../VentoryUI/components/common/File/Image';
import { FileUtils, FilePreview, FileEntityType } from '../../../../../types/file';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { TagRelationAuthorization } from '../../../../../types/tag/tagRelation';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { UnitOfMeasureUtil } from '../../../../../types/uom';
import { UserContext } from '../../../../../context/UserContext';
import { ContactContext } from '../../../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { Unilever } from '../../../../../types/customer/DHL/Unilever/Unilever';
import { Checkbox } from '../../../../components/Checkbox';
import { DropdownSearchSelect, DropdownSelect } from '../../../../components/Dropdown';
import { ProductMasterDataInput } from '../../../../../types/product_master_data';
import { TextInput } from '../../../../components/TextInput';
import { IconType } from '../../../../icons/Icon';
import { Intuitive } from '../../../../../types/customer/intuitive/Intuitive';
import { IconButton } from '../../../../icons';

interface ProductMasterDataInfoPaneInputProps {
  input: ProductMasterDataInput;
  setInput: (pmd: ProductMasterDataInput) => void;

  filesInput?: Map<string, File> | null;
  uploadFilesComponent?: React.ReactNode;

  tagInput?: React.ReactNode;
}

export default function ProductMasterDataInfoPane({
  input,
  setInput,
  filesInput,
  uploadFilesComponent,
  tagInput,
}: ProductMasterDataInfoPaneInputProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { customFields } = useContext(CustomFieldContext);
  const { files } = useContext(FileContext);
  const { hasRole, isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const [displayImages, setDisplayImages] = useState<(string | FilePreview)[]>([]);

  const cf = useMemo(() => {
    return [...customFields.values()]
      .filter(cf => cf.entityType === CustomFieldEntityType.productMasterData && !cf.hidden)
      .sort((c1, c2) => c1.name.localeCompare(c2.name));
  }, [customFields]);

  const handleDisplayImages = async () => {
    const existingImageIds = [...files.values()]
      .filter(f => f.fileTypeCategory === 'image' && f.entityId === input.id)
      .map(f => f.id);

    if (!filesInput?.size) return setDisplayImages([...existingImageIds]);

    const previewImages: FilePreview[] = await Promise.all(
      [...filesInput.entries()]
        .filter(([id, file]) => FileUtils.getFileTypeCategory(file) === 'image')
        .map(async ([id, file]) => {
          return {
            id: id,
            file: file,
            name: file.name,
            size: FileUtils.fileSizeToFormattedString(file.size, 1),
            type: file.type,
            data: await FileUtils.fileToBase64(file, { prefix: true }),
          };
        }),
    );

    setDisplayImages([...existingImageIds, ...previewImages]);
  };

  useEffect(() => {
    handleDisplayImages();
  }, [files, filesInput, input]);

  const hasExpiryManagement = currentCompany.settings.featureToggles.lots.expiryManagement;

  const isExternal = (cf: any) => {
    if (Unilever.companyIds.includes(cf.companyId)) return false;
    return !!cf.externalIdentifier;
  };

  const computeUnitTypeOptions = () => {
    if (input.kitManaged) return [UnitType.quantity];
    return Object.values(UnitType);
  };

  const generateRandomProductNumber = () => {
    // Implement the logic to generate a random product number
    const randomProductNumber = `${Math.floor(Math.random() * 1000000)}`;
    setInput(input.withProductNumber(randomProductNumber));
  };

  return (
    <div className='content-x grid h-full grid-cols-3 gap-8 overflow-auto'>
      {/* Column 1 */}
      <div className='flex flex-col gap-4'>
        <div className='grid gap-2'>
          <p className='section-title'>{translate({ key: TranslationKey.general })}</p>

          <TextInput
            mandatory
            disabled={!!input.externalIdentifier || !isAdmin}
            value={input.productNumber}
            label={translate({ key: TranslationKey.productNumber })}
            placeholder={translate({ key: TranslationKey.productNumber })}
            onChange={v => setInput(input.withProductNumber(v))}
            trailing={
              Intuitive.companyIds.includes(currentCompany.id) && !input.id ? (
                <IconButton
                  type={IconType.refreshCw5}
                  stroke='stroke-body'
                  className='hover:bg-transparent'
                  onClick={generateRandomProductNumber}
                />
              ) : undefined
            }
          />

          <TextInput
            mandatory
            disabled={!!input.externalIdentifier || !isAdmin}
            value={input.productName}
            label={translate({ key: TranslationKey.productName })}
            placeholder={translate({ key: TranslationKey.productName })}
            onChange={v => setInput(input.withProductName(v))}
          />

          <TextInput
            disabled={!!input.externalIdentifier || !isAdmin}
            label={translate({ key: TranslationKey.globalTradeItemNumber }).toUpperCase()}
            placeholder={translate({ key: TranslationKey.globalTradeItemNumber }).toUpperCase()}
            onChange={v => setInput(input.withEAN(v))}
            value={input.globalTradeItemNumber || ''}
          />

          <DropdownSearchSelect
            placeholder={translate({ key: TranslationKey.countryOfOrigin })}
            label={translate({ key: TranslationKey.countryOfOrigin })}
            value={countries.find(c => c.cca2 === input.countryOfOrigin) || null}
            disabled={!!input.externalIdentifier || !isAdmin}
            items={countries}
            textBuilder={(item: any) => item.name.common}
            onSelect={(item: any) => setInput(input.withCountryOfOrigin(item ? item.cca2 : undefined))}
          />
        </div>

        <div className='grid gap-2'>
          <p className='section-title'>{translate({ key: TranslationKey.productManagement })}</p>

          <div className='grid gap-1'>
            <Checkbox
              disabled={!!input.externalIdentifier || !isAdmin}
              label={translate({ key: TranslationKey.serial })}
              onChange={v => setInput(input.withSerialManaged(v))}
              value={input.serialManaged || false}
            />
            <Checkbox
              disabled={!!input.externalIdentifier || !isAdmin}
              label={translate({ key: TranslationKey.lpn }).toUpperCase()}
              onChange={v => setInput(input.withLpnManaged(v))}
              value={input.lpnManaged || false}
            />
            {currentCompany.settings.featureToggles.kits?.kits && (
              <Checkbox
                disabled={!!input.externalIdentifier || !isAdmin}
                label={translate({ key: TranslationKey.kit })}
                onChange={v => setInput(input.withKitManaged(v))}
                value={input.kitManaged || false}
              />
            )}
            <Checkbox
              disabled={!!input.externalIdentifier || !isAdmin}
              label={translate({ key: TranslationKey.handlingUnit })}
              onChange={v => setInput(input.withHandlingUnit(v))}
              value={input.handlingUnit || false}
            />
            <Checkbox
              disabled={!!input.externalIdentifier || !isAdmin}
              label={translate({ key: TranslationKey.lot })}
              onChange={v => setInput(input.withLotManaged(v))}
              value={input.lotManaged || false}
            />
            {input.lotManaged && hasExpiryManagement ? (
              <Checkbox
                disabled={!!input.externalIdentifier || !isAdmin}
                label={translate({ key: TranslationKey.expiryTracking })}
                onChange={v => setInput(input.withExpiryTracking(v))}
                value={input.expiryTracking || false}
                tooltip={translate({ key: TranslationKey.expiryTrackingTooltip })}
              />
            ) : null}
          </div>

          <div className='grid gap-2'>
            <DropdownSelect<UnitType>
              disabled={!!input.externalIdentifier || !isAdmin}
              label={translate({ key: TranslationKey.unitOfMeasure })}
              items={computeUnitTypeOptions()}
              value={input.unitOfMeasure?.unitType || UnitType.quantity}
              textBuilder={item => UnitOfMeasureUtil.unitTypeToString(item)}
              onSelect={item => setInput(input.withUnitType(item))}
            />

            {input.unitOfMeasure?.unitType && !UnitOfMeasureUtil.needsUnit(input.unitOfMeasure?.unitType) ? (
              <>
                <DropdownSelect<UnitSystem>
                  disabled={!!input.externalIdentifier}
                  items={Array.from(Object.values(UnitSystem))}
                  value={input.unitOfMeasure?.system || UnitSystem.metric}
                  textBuilder={item => translate({ key: item })}
                  onSelect={item => setInput(input.withUnitSystem(item))}
                />

                <DropdownSelect
                  disabled={!!input.externalIdentifier}
                  items={UnitOfMeasureUtil.unitValues(input.unitOfMeasure)}
                  value={input.unitOfMeasure?.unit || UnitOfMeasureUtil.defaultUnit(input.unitOfMeasure)}
                  textBuilder={item => UnitOfMeasureUtil.unitText(item, input.unitOfMeasure)}
                  onSelect={item => setInput(input.withUnit(item))}
                />
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-4'>
        <p className='section-title'>{translate({ key: TranslationKey.image, count: 'plural' })}</p>
        {isAdmin ? (
          <div className='flex flex-col gap-2'>
            <p className='text-sm'>{translate({ key: TranslationKey.productImage })}</p>
            <ImagePicker
              fileEntityType={FileEntityType.pmd}
              width='100%'
              onRemove={() => setInput(input.withImage(undefined))}
              fileId={input.image || ''}
              onSelected={selected => setInput(input.withImage(selected))}
              fileSasIdsOrPreviews={displayImages}
            />
          </div>
        ) : null}
        <div>{uploadFilesComponent || null}</div>
      </div>

      <div className='flex flex-col gap-4'>
        {hasRole(new TagRelationAuthorization().forCreate()) && (
          <div className='grid gap-2'>
            <p className='section-title'>{translate({ key: TranslationKey.tag, count: 'plural' })}</p>
            {tagInput}
          </div>
        )}
        {cf.length ? (
          <div className='grid gap-2'>
            <p className='section-title'>{translate({ key: TranslationKey.customFields })}</p>
            {cf.map(cf => (
              <div key={cf.id}>
                <CustomFieldInput
                  disabled={isExternal(cf) || cf.generated || !isAdmin}
                  value={WithCustomFields.toCustomFieldsMap(input).get(cf.id)?.value}
                  entity={input}
                  item={customFields.get(cf.id)}
                  onChange={setInput}
                  users={companyUsers}
                  contacts={contacts}
                  productMasterData={productMasterData}
                />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
